/**
 * Files for the organisation (ADR-0049): whether its people may connect
 * cloud storage, the storage servers they may connect to — its own Seafile,
 * Nextcloud and ownCloud, and Genworx's when Genworx offers them — and who
 * has connected what. People connect by signing in on the provider's own
 * page; nothing here holds a storage password.
 */

import { useContext, useState, type FormEvent } from 'react';
import type { StorageAdminOverview, StorageKind } from 'zitimail-shared-types';

import { KIND_NAMES } from '../api/storage';
import { api } from './api';
import { AdminWhereContext } from './mailboxes';
import { ActionButton, Badge, CopyButton, Loaded, useLoad, when } from './ui';

const KINDS: readonly StorageKind[] = ['seafile', 'nextcloud', 'ocis'];

const HINTS: Record<StorageKind, string> = {
  seafile:
    'Seafile Community or Pro. Its administrator sets CLIENT_SSO_VIA_LOCAL_BROWSER = True in seahub_settings.py, so people can sign in through the browser as Seafile’s own desktop app does.',
  nextcloud:
    'Nextcloud signs people in with its login flow, the one its desktop and phone apps use, through whatever sign-in Nextcloud has (SSO, LDAP).',
  ocis: 'ownCloud Infinite Scale signs in with OpenID Connect. Register a client for ZitiOffice at the identity provider ownCloud trusts, with the return address below.',
};

function AddServer({
  slug,
  callbackUrl,
  onAdded,
}: {
  slug: string;
  callbackUrl: string | null;
  onAdded: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [kind, setKind] = useState<StorageKind>('seafile');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [sameAddress, setSameAddress] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`${base(slug)}/storage/servers`, {
        method: 'POST',
        body: {
          kind,
          name: name.trim(),
          base_url: url.trim(),
          require_same_address: sameAddress,
          ...(kind === 'ocis'
            ? {
                oidc_issuer: issuer.trim() || undefined,
                oidc_client_id: clientId.trim(),
                oidc_client_secret: clientSecret.trim() || undefined,
              }
            : {}),
        },
      });
      setName('');
      setUrl('');
      setIssuer('');
      setClientId('');
      setClientSecret('');
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Not added.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="adm-form adm-sub" onSubmit={(e) => void submit(e)}>
      <h4>Add a storage server</h4>
      <div className="adm-form-grid">
        <label>
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as StorageKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k === 'ocis' ? 'ownCloud Infinite Scale' : KIND_NAMES[k]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Name people see
          <input
            value={name}
            maxLength={80}
            required
            placeholder="Acme Files"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Address
          <input
            className="adm-mono"
            value={url}
            required
            type="url"
            placeholder="https://files.example.com"
            spellCheck={false}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
        {kind === 'ocis' && (
          <>
            <label>
              <span>
                Identity provider{' '}
                <span className="adm-muted">(issuer; blank: the server itself)</span>
              </span>
              <input
                className="adm-mono"
                value={issuer}
                type="url"
                placeholder="https://login.example.com/realms/acme"
                spellCheck={false}
                onChange={(e) => setIssuer(e.target.value)}
              />
            </label>
            <label>
              Client ID
              <input
                className="adm-mono"
                value={clientId}
                required
                spellCheck={false}
                onChange={(e) => setClientId(e.target.value)}
              />
            </label>
            <label>
              <span>
                Client secret <span className="adm-muted">(if the client has one)</span>
              </span>
              <input
                className="adm-mono"
                value={clientSecret}
                type="password"
                autoComplete="off"
                onChange={(e) => setClientSecret(e.target.value)}
              />
            </label>
          </>
        )}
      </div>
      <p className="adm-muted adm-small">{HINTS[kind]}</p>
      {kind === 'ocis' && (
        <p className="adm-small">
          Return address to register:{' '}
          {callbackUrl ? (
            <>
              <code className="adm-mono">{callbackUrl}</code> <CopyButton text={callbackUrl} />
            </>
          ) : (
            <span className="adm-error">not set up on this platform yet — ask Genworx</span>
          )}
        </p>
      )}
      <label className="adm-check">
        <input
          type="checkbox"
          checked={sameAddress}
          onChange={(e) => setSameAddress(e.target.checked)}
        />{' '}
        Only accounts with the person’s own email address{' '}
        <span className="adm-muted">
          (recommended: a sign-in link sent to someone else cannot connect their files)
        </span>
      </label>
      <div className="adm-inline">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Checking the server…' : 'Add server'}
        </button>
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

export function StoragePanel({ slug }: { slug: string }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<StorageAdminOverview>(`${base(slug)}/storage`, { signal }),
    `storage:${slug}`,
  );
  const setPolicy = async (enabled: boolean, allowUnscanned: boolean) => {
    await api(`${base(slug)}/storage/policy`, {
      method: 'PUT',
      body: { enabled, allow_unscanned: allowUnscanned },
    });
    reload();
  };

  return (
    <section className="adm-panel">
      <h3>Files</h3>
      <Loaded load={load}>
        {(o) => (
          <>
            {!o.available && (
              <p className="adm-error">Files is not set up on this platform yet. Ask Genworx.</p>
            )}
            <div className="adm-inline">
              <span>
                {o.policy.enabled ? <Badge tone="ok">On</Badge> : <Badge tone="neutral">Off</Badge>}
              </span>
              <ActionButton
                tone="ghost"
                run={() => setPolicy(!o.policy.enabled, o.policy.allow_unscanned)}
              >
                {o.policy.enabled ? 'Turn off Files' : 'Turn on Files'}
              </ActionButton>
            </div>
            <p className="adm-muted adm-small">
              With Files on, people connect their storage in webmail and the apps (the Files button
              in the rail), attach files from it as a copy or a link, and save attachments to it.
              Every file moved either way is checked for malware first; programs and scripts are
              never attached or linked from storage.
            </p>
            <label className="adm-check">
              <input
                type="checkbox"
                checked={o.policy.allow_unscanned}
                onChange={(e) => void setPolicy(o.policy.enabled, e.target.checked)}
              />{' '}
              Let files over {Math.round(o.scanner / 1024 ** 2)} MB through, marked as not checked{' '}
              <span className="adm-muted">(they are too large to scan whole; off: refused)</span>
            </label>

            <h4>Storage servers</h4>
            {o.servers.length === 0 ? (
              <p className="adm-muted">None yet. Add your organisation’s server below.</p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kind</th>
                    <th>Address</th>
                    <th>Own address only</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {o.servers.map((s) => (
                    <tr key={s.id}>
                      <td>
                        {s.name} {s.genworx && <Badge tone="neutral">Genworx</Badge>}{' '}
                        {!s.enabled && <Badge tone="neutral">Off</Badge>}
                      </td>
                      <td>{KIND_NAMES[s.kind]}</td>
                      <td className="adm-mono">{s.base_url}</td>
                      <td>
                        <span className="adm-inline">
                          {s.require_same_address ? 'Yes' : 'No'}
                          {!s.genworx && (
                            <ActionButton
                              tone="ghost"
                              run={async () => {
                                if (
                                  s.require_same_address &&
                                  !window.confirm(
                                    `Let people connect ${s.name} accounts under any address? Do this for a server whose accounts use another organisation's addresses; a sign-in link sent to someone else could then connect their files.`,
                                  )
                                ) {
                                  return;
                                }
                                await api(`${base(slug)}/storage/servers/${s.id}`, {
                                  method: 'PATCH',
                                  body: { require_same_address: !s.require_same_address },
                                });
                                reload();
                              }}
                            >
                              {s.require_same_address ? 'Allow any account' : 'Own address only'}
                            </ActionButton>
                          )}
                        </span>
                      </td>
                      <td>
                        {!s.genworx && (
                          <span className="adm-inline">
                            <ActionButton
                              tone="ghost"
                              run={async () => {
                                const name = window.prompt('The name people see', s.name)?.trim();
                                if (!name || name === s.name) {
                                  return;
                                }
                                await api(`${base(slug)}/storage/servers/${s.id}`, {
                                  method: 'PATCH',
                                  body: { name },
                                });
                                reload();
                              }}
                            >
                              Rename
                            </ActionButton>
                            <ActionButton
                              tone="ghost"
                              run={async () => {
                                await api(`${base(slug)}/storage/servers/${s.id}`, {
                                  method: 'PATCH',
                                  body: { enabled: !s.enabled },
                                });
                                reload();
                              }}
                            >
                              {s.enabled ? 'Stop offering' : 'Offer again'}
                            </ActionButton>
                            <ActionButton
                              tone="danger"
                              run={async () => {
                                if (
                                  !window.confirm(
                                    `Remove ${s.name}? Everyone connected to it is disconnected; their files stay on the server.`,
                                  )
                                ) {
                                  return;
                                }
                                await api(`${base(slug)}/storage/servers/${s.id}`, {
                                  method: 'DELETE',
                                });
                                reload();
                              }}
                            >
                              Remove
                            </ActionButton>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <AddServer slug={slug} callbackUrl={o.callback_url} onAdded={reload} />

            <h4>Connections</h4>
            {o.accounts.length === 0 ? (
              <p className="adm-muted">Nobody has connected storage yet.</p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Server</th>
                    <th>Signed in as</th>
                    <th>Connected</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {o.accounts.map((a) => (
                    <tr key={a.id}>
                      <td className="adm-mono">{a.owner}</td>
                      <td>{a.server_name}</td>
                      <td className="adm-mono">{a.remote_name}</td>
                      <td>{when(a.connected_at)}</td>
                      <td>{when(a.last_used_at)}</td>
                      <td>
                        <ActionButton
                          tone="ghost"
                          run={async () => {
                            await api(`${base(slug)}/storage/accounts/${a.id}`, {
                              method: 'DELETE',
                            });
                            reload();
                          }}
                        >
                          Disconnect
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Loaded>
    </section>
  );
}
